import type { AudioProcessor } from "@twilio/voice-sdk";

/**
 * Streams an <audio> element into the call via Web Audio.
 * - createProcessedStream() returns a MediaStream the Voice SDK will treat as the "mic".
 * - play()/pause()/setVolume() control the element.
 */
export class BackgroundAudioProcessor implements AudioProcessor {
	private ctx: AudioContext | null = null;
	private element?: HTMLAudioElement;
	private source?: MediaElementAudioSourceNode;
	private dest?: MediaStreamAudioDestinationNode;

	constructor(private url: string) {}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async createProcessedStream(_micStream: MediaStream): Promise<MediaStream> {

		console.log("[BackgroundAudioProcessor:createProcessedStream] Called", this.url);
		if (!this.ctx) {
			console.log("[BackgroundAudioProcessor:createProcessedStream] Create AudioContext", this.url)
			this.ctx = new AudioContext();
		}

		const el = new Audio();

		// If media is cross-origin, server must send CORS headers
		el.crossOrigin = "anonymous";
		el.src = this.url;
		el.preload = "auto";
		this.element = el;

		// --- START temporary logging ---
		el.addEventListener("canplay", (event) => {
			console.log("[BackgroundAudioProcessor:oncanplay] Audio can start, but not sure it will play through.", JSON.stringify(event));
		});
		el.addEventListener("canplaythrough", (event) => {
			console.log("[BackgroundAudioProcessor:oncanplaythrough] Audio can play through the entire file without having to stop to buffer.", JSON.stringify(event));
		});
		el.addEventListener("emptied", (event) => {
			console.log("[BackgroundAudioProcessor:onemptied] Audio is empty, was load() called?", JSON.stringify(event));
		});
		el.addEventListener("loadeddata", (event) => {
			console.log("[BackgroundAudioProcessor:onloadeddata] First frame of the audio has finished loading.", JSON.stringify(event));
		});
		el.addEventListener("loadedmetadata", (event) => {
			console.log("[BackgroundAudioProcessor:onloadedmetadata] Audio metadata has been loaded.", JSON.stringify(event));
		});
		el.addEventListener("loadstart", (event) => {
			console.log("[BackgroundAudioProcessor:onloadstart] Browser has started to load the audio resource.", JSON.stringify(event));
		});
		el.addEventListener("play", (event) => {
			console.log("[BackgroundAudioProcessor:onplay] Playback has started.", JSON.stringify(event));
		});
		el.addEventListener("stalled", (event) => {
			console.log("[BackgroundAudioProcessor:onstalled] Browser failed to fetch data, but is trying.", JSON.stringify(event));
		});
		el.addEventListener("suspend", (event) => {
			console.log("[BackgroundAudioProcessor:onsuspend] Audio data loading has been suspended.", JSON.stringify(event));
		});
		el.addEventListener("waiting", (event) => {
			console.log("[BackgroundAudioProcessor:onwaiting] Playback has stopped because of a temporary lack of data.", JSON.stringify(event));
		});
		// --- END temporary logging ---

		const src = this.ctx.createMediaElementSource(el);
		const dst = this.ctx.createMediaStreamDestination();
		this.source = src;
		this.dest = dst;

		// To monitor locally, also connect to destination:
		console.log("[BackgroundAudioProcessor:createProcessedStream] Create local monitoring destination");
		src.connect(this.ctx.destination);

		console.log("[BackgroundAudioProcessor:createProcessedStream] Create streaming destination");
		src.connect(dst);

		// Return the stream Twilio will capture/encode
		console.log("[BackgroundAudioProcessor:createProcessedStream] Return local monitoring destination");
		return dst.stream;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async destroyProcessedStream(_processed: MediaStream): Promise<void> {
		try {
			this.source?.disconnect();
		} catch (err) {
			console.warn("[BackgroundAudioProcessor:destroyProcessedStream] Failed to disconnect source", JSON.stringify(err));
		}

		try {
			this.dest?.disconnect();
		} catch (err) {
			console.warn("[BackgroundAudioProcessor:destroyProcessedStream] Failed to disconnect dest", JSON.stringify(err));
		}

		try {
			this.element?.pause();
		} catch (err) {
			console.warn("[BackgroundAudioProcessor:destroyProcessedStream] Failed to pause element", JSON.stringify(err));
		}

		this.source = undefined;
		this.dest = undefined;
		this.element = undefined;

		// Keep AudioContext alive in case we reuse the processor
		// await this.ctx?.close(); this.ctx = null;
	}

	async play(): Promise<void> {

		console.log("[BackgroundAudioProcessor:play] Called", JSON.stringify(this.ctx));

		if (!this.ctx) {
			return;
		}

		try {
			await this.ctx.resume();
		} catch (err) {
			console.warn("[BackgroundAudioProcessor:play] Failed to resume", JSON.stringify(err));
		}

		try {
			await this.element?.play();
		} catch (err) {
			console.warn("[BackgroundAudioProcessor:play] Failed to play", JSON.stringify(err));
		}
	}

	pause() {
		this.element?.pause();
	}

	setVolume(v: number) {
		if (this.element) {
			this.element.volume = v;
		}
	}

	getElement(): HTMLAudioElement | undefined {
		return this.element;
	}

	updateUrl(next: string) {
		if (this.element) {
			this.element.src = next;
		}
	}
}
